import React from 'react';

export interface LocationCardProps {
  latitude: number | null;
  longitude: number | null;
  /** Section heading. */
  title?: string;
  /** Copy for the empty state when there are no coordinates. */
  emptyMessage?: string;
  /**
   * 'card' (default) draws its own bordered card; 'plain' renders bare so it
   * can sit as a row inside an existing card.
   */
  variant?: 'card' | 'plain';
  className?: string;
}

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const buildEmbedUrl = (lat: number, lng: number): string => {
  // Prefer the official Maps Embed API when a key is configured;
  // fall back to the keyless embed otherwise.
  if (
    GOOGLE_MAPS_API_KEY &&
    GOOGLE_MAPS_API_KEY !== 'your_google_maps_api_key'
  ) {
    return `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${lat},${lng}&zoom=16`;
  }
  return `https://maps.google.com/maps?q=${lat},${lng}&z=16&output=embed`;
};

const buildExternalUrl = (lat: number, lng: number): string =>
  `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

const MapsLink: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => (
  <a
    href={buildExternalUrl(lat, lng)}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 text-[13px] font-medium text-primary hover:underline"
  >
    Open in Google Maps
    <i className="ri-external-link-line text-[14px]"></i>
  </a>
);

const Coordinates: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => (
  <div className="flex items-center gap-2">
    <i className="ri-map-pin-2-line text-[16px] text-[#F04438]"></i>
    <span className="font-mono text-[13px] text-gray-700">
      {lat.toFixed(5)}, {lng.toFixed(5)}
    </span>
  </div>
);

/**
 * Last known GPS fix rendered as an embedded Google map, with a link out to
 * Google Maps and the raw coordinates underneath. Falls back to an empty
 * state when either coordinate is missing.
 */
const LocationCard: React.FC<LocationCardProps> = ({
  latitude,
  longitude,
  title = 'Last Known Location',
  emptyMessage = 'No location data available.',
  variant = 'card',
  className = '',
}) => {
  const hasCoords =
    typeof latitude === 'number' &&
    typeof longitude === 'number' &&
    !Number.isNaN(latitude) &&
    !Number.isNaN(longitude);
  const lat = latitude as number;
  const lng = longitude as number;

  const map = (
    <iframe
      title="Vehicle last known location"
      src={hasCoords ? buildEmbedUrl(lat, lng) : undefined}
      className="h-full w-full border-0"
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      allowFullScreen
    />
  );

  if (variant === 'plain') {
    return (
      <div className={className}>
        <div className="mb-1.5 flex items-center justify-between gap-3">
          <div className="text-xs text-gray-400">{title}</div>
          {hasCoords && <MapsLink lat={lat} lng={lng} />}
        </div>

        {hasCoords ? (
          <>
            <div className="h-[200px] w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
              {map}
            </div>
            <div className="mt-2">
              <Coordinates lat={lat} lng={lng} />
            </div>
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-[13px] text-gray-400">
            {emptyMessage}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`overflow-hidden rounded-xl border border-gray-200 bg-white ${className}`}
    >
      <div className="flex items-center justify-between gap-3 px-5 py-4">
        <h2 className="text-[15px] font-semibold text-gray-900">{title}</h2>
        {hasCoords && <MapsLink lat={lat} lng={lng} />}
      </div>

      {hasCoords ? (
        <>
          <div className="h-[260px] w-full bg-gray-100">{map}</div>
          <div className="px-5 py-3">
            <Coordinates lat={lat} lng={lng} />
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center px-5 py-10 text-center">
          <i className="ri-map-pin-line mb-2 text-[28px] text-gray-300"></i>
          <p className="text-[13px] text-gray-400">{emptyMessage}</p>
        </div>
      )}
    </div>
  );
};

export default LocationCard;
